import React, { useRef } from 'react';

import clsx from 'clsx';
import { Link } from '../../../atoms/link/Link';
import RichText from '../../../atoms/richText';
import { Heading } from '../../../typography/heading/Heading';
import { Text } from '../../../typography/text/Text';
import Media from '../../media';
import { theme } from './_vars';
import styles from './ArticleItem.module.scss';

const heightTo: Record<string, string> = {
  'Only media': 'onlyMedia',
  'Full block': 'fullBlock',
};

export interface ArticleItemProps {
  media?: any;
  title?: string;
  description?: string | object;
  textAlign?: 'right' | 'left' | 'center';
  link?: string;
  linkTitle?: string;
  isLinkWrapped?: boolean;
  width?: string | number;
  mediaTitlePosition?: 'left' | 'right' | 'top';
  mediaTitle?: string;
  verticalAlign?: 'top' | 'bottom' | 'center' | 'stretch';
  gridType?: 'asSlider' | 'asDefault' | 'asGridOnMobile';
  subtitle?: string;
  urlPosition?: string;
  blockHeight?: string | number;
  applyHeightTo?: string;
  itemsCount?: number;
}

const ArticleItem: React.FC<ArticleItemProps> = ({
  media,
  title,
  description,
  textAlign = 'left',
  link,
  linkTitle,
  isLinkWrapped,
  width = '100%',
  mediaTitlePosition = 'top',
  mediaTitle,
  verticalAlign = 'stretch',
  gridType,
  subtitle,
  urlPosition,
  blockHeight,
  applyHeightTo,
}) => {
  const textBlockRef = useRef<any>();
  const textBlockRect = textBlockRef?.current?.getBoundingClientRect();
  const itemWidth = gridType === 'asSlider' ? '100%' : `${width}%` || '100%';
  const isFullBlock = blockHeight && heightTo[applyHeightTo] === 'fullBlock';
  const isOnlyMedia = blockHeight && heightTo[applyHeightTo] === 'onlyMedia';
  const hasOnlyMediaAndLink = media && link && linkTitle && !title && !description && !subtitle;
  const isExternalLink = link?.startsWith('http') || link?.startsWith('https') || !link?.startsWith('/');

  const renderMedia = (media, mediaTitle, mediaTitlePosition, theme, link, isLinkWrapped) => {
    if (!mediaTitle && !media) return null;

    const MediaComponent = (
      <Media
        media={media}
        customClasses={{ controls: styles.controlsWrapper }}
        showControls={media?.showControls}
        muted={media?.muted}
        isSSR={undefined}
        wrappedContent={undefined}
      />
    );

    return (
      <div
        className={clsx(styles.media, {
          [`${styles[`position_${mediaTitlePosition}`]}`]: mediaTitlePosition && mediaTitlePosition !== 'top',
        })}
      >
        {mediaTitlePosition !== 'top' && mediaTitle && (
          <Heading
            className={styles.mediaTitleVer}
            as="h2"
            placement="plp"
            textTransform={theme === 'jag' ? 'initial' : 'uppercase'}
            weight={theme === 'saba' ? 'light' : 'semibold'}
          >
            <span>{mediaTitle}</span>
          </Heading>
        )}
        {link && isLinkWrapped ? (
          isExternalLink ? (
            <a href={link} target="_blank" rel="noopener noreferrer" title={linkTitle}>
              {MediaComponent}
            </a>
          ) : (
            <Link to={link}>{MediaComponent}</Link>
          )
        ) : (
          MediaComponent
        )}
      </div>
    );
  };

  const hasMedia = media && Object.values(media).some((img: any) => img?.url);

  return (
    <div
      style={
        {
          '--article-width': itemWidth,
          '--article-height': isFullBlock ? `${blockHeight}px` : verticalAlign === 'stretch' ? '100%' : 'initial',
          '--article-media-height': isOnlyMedia ? `${blockHeight}px` : verticalAlign === 'stretch' ? '100%' : 'initial',
          '--article-text-block-height': `${textBlockRect?.height}px`,
        } as React.CSSProperties
      }
      data-text-height={textBlockRect?.height}
      className={clsx(
        styles.alignedWrapper,
        styles[blockHeight ? 'vAlign_top' : `vAlign_${verticalAlign}`],
        styles[`wrap_position_${mediaTitlePosition}`],
        {
          [`${styles.withoutMedia}`]: !media || !hasMedia,
          [`${styles.fixedBlockHeight}`]: isFullBlock,
          [styles.hasMediaTitle]: mediaTitlePosition === 'left' || mediaTitlePosition === 'right',
        },
      )}
    >
      {mediaTitle && mediaTitlePosition === 'top' && (
        <div className={clsx(styles.verticalTitle, styles[`position_${mediaTitlePosition}`])}>
          <Heading
            className={styles.mediaTitleVer}
            as="h2"
            placement="plp"
            textTransform={theme === 'jag' ? 'initial' : 'uppercase'}
            weight={theme === 'saba' ? 'light' : 'semibold'}
          >
            {mediaTitle}
          </Heading>
        </div>
      )}
      {(media || link || linkTitle || subtitle || title || description || mediaTitle) && (
        <div
          className={clsx(styles.container, styles[`align_${textAlign}`], {
            [styles.hasOnlyMediaAndLink]: hasOnlyMediaAndLink,
          })}
        >
          {renderMedia(media, mediaTitle, mediaTitlePosition, theme, link, isLinkWrapped)}
          <div ref={textBlockRef} className={styles.textBlock}>
            {link && linkTitle && (
              <div className={clsx(styles.linkWrapper, urlPosition ? styles[`position_${urlPosition}`] : '')}>
                {isExternalLink ? (
                  <a
                    href={link}
                    className={clsx(styles.link, urlPosition ? styles[`position_${urlPosition}`] : '')}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={linkTitle}
                  >
                    {linkTitle}
                  </a>
                ) : (
                  <Link className={clsx(styles.link, urlPosition ? styles[`position_${urlPosition}`] : '')} link={link}>
                    {linkTitle}
                  </Link>
                )}
              </div>
            )}
            {title && (
              <Text className={styles.title} as="h2" size="xl" weight={theme === 'saba' ? 'light' : 'semibold'}>
                {title}
              </Text>
            )}
            {subtitle && (
              <Text
                className={styles.subtitle}
                as="h4"
                size="xl"
                weight={theme === 'saba' ? 'light' : theme === 'jag' ? 'semibold' : 'normal'}
              >
                {subtitle}
              </Text>
            )}
            {description && (
              <div className={styles.description}>
                <RichText content={description} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleItem;
